// ---------------------------------------------------------------------------
// ROI Calculator Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, jsString, text } from "../escape.js";
import { renderConversionActionControl } from "../conversion-action.js";

interface RoiCalculatorProps {
  id?: string;
  heading?: string;
  description?: string;
  primaryLabel: string;
  primaryMin: number;
  primaryMax: number;
  primaryDefault: number;
  primaryStep: number;
  primaryPrefix?: string;
  primarySuffix?: string;
  secondaryLabel: string;
  secondaryMin: number;
  secondaryMax: number;
  secondaryDefault: number;
  secondaryStep: number;
  secondaryPrefix?: string;
  secondarySuffix?: string;
  formula: "multiply" | "percentage" | "compound";
  multiplier: number;
  resultLabel: string;
  resultPrefix?: string;
  resultSuffix?: string;
  ctaText?: string;
}

function buildScript(uid: string): string {
  return `<script>(function(){var root=document.querySelector('[data-ease-roi-id="${jsString(uid)}"]');if(!root)return;var primary=root.querySelector('[data-ease-roi-input="primary"]');var secondary=root.querySelector('[data-ease-roi-input="secondary"]');var primaryValue=root.querySelector('[data-ease-roi-value="primary"]');var secondaryValue=root.querySelector('[data-ease-roi-value="secondary"]');var result=root.querySelector('[data-ease-roi-value="result"]');if(!(primary instanceof HTMLInputElement)||!(secondary instanceof HTMLInputElement)||!primaryValue||!secondaryValue||!result)return;function fmt(value){return new Intl.NumberFormat('en-US',{maximumFractionDigits:value>=1000?0:1}).format(value);}function compute(primaryRaw,secondaryRaw,formula,multiplier){if(formula==='percentage')return primaryRaw*(secondaryRaw/100)*multiplier;if(formula==='compound')return primaryRaw*Math.pow(1+(secondaryRaw/100),multiplier);return primaryRaw*secondaryRaw*multiplier;}function sync(){var primaryRaw=Number(primary.value);var secondaryRaw=Number(secondary.value);var formula=root.getAttribute('data-ease-roi-formula')||'multiply';var multiplier=Number(root.getAttribute('data-ease-roi-multiplier')||'1');var primaryPrefix=root.getAttribute('data-ease-roi-primary-prefix')||'';var primarySuffix=root.getAttribute('data-ease-roi-primary-suffix')||'';var secondaryPrefix=root.getAttribute('data-ease-roi-secondary-prefix')||'';var secondarySuffix=root.getAttribute('data-ease-roi-secondary-suffix')||'';var resultPrefix=root.getAttribute('data-ease-roi-result-prefix')||'';var resultSuffix=root.getAttribute('data-ease-roi-result-suffix')||'';primaryValue.textContent=primaryPrefix+fmt(primaryRaw)+primarySuffix;secondaryValue.textContent=secondaryPrefix+fmt(secondaryRaw)+secondarySuffix;result.textContent=resultPrefix+fmt(compute(primaryRaw,secondaryRaw,formula,multiplier))+resultSuffix;}primary.addEventListener('input',sync);secondary.addEventListener('input',sync);sync();})()</script>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as RoiCalculatorProps;
  const uid = `roi-${String(p.id ?? p.heading ?? "calculator").replace(/[^a-z0-9]/gi, "").slice(0, 24)}`;
  const heading = p.heading ?? "Estimate the upside";
  const description = p.description ?? "";
  const ctaHtml = p.ctaText
    ? renderConversionActionControl({
      props,
      label: p.ctaText,
      className: "ease-btn ease-btn-primary",
      defaultUrl: "#form",
      componentType: "roi-calculator",
      componentId: p.id,
    })
    : "";

  return `<section class="ease-section ease-roi-calculator" aria-label="${attr(heading)}" data-ease-roi-id="${attr(uid)}" data-ease-roi-formula="${attr(p.formula ?? "multiply")}" data-ease-roi-multiplier="${attr(String(p.multiplier ?? 1))}" data-ease-roi-primary-prefix="${attr(p.primaryPrefix ?? "")}" data-ease-roi-primary-suffix="${attr(p.primarySuffix ?? "")}" data-ease-roi-secondary-prefix="${attr(p.secondaryPrefix ?? "")}" data-ease-roi-secondary-suffix="${attr(p.secondarySuffix ?? "")}" data-ease-roi-result-prefix="${attr(p.resultPrefix ?? "")}" data-ease-roi-result-suffix="${attr(p.resultSuffix ?? "")}">
  <div class="ease-roi-shell">
    <div class="ease-roi-copy" style="text-align:center;margin-bottom:var(--ease-spacing-lg,2rem);">
      <h2 class="ease-roi-heading">${text(heading)}</h2>
      ${description ? `<p class="ease-roi-description">${text(description)}</p>` : ""}
    </div>
    <div class="ease-roi-controls" style="display:grid;gap:var(--ease-spacing-lg,2rem);">
      <label class="ease-roi-control" style="display:grid;gap:var(--ease-spacing-sm,0.75rem);">
        <span style="display:flex;justify-content:space-between;gap:1rem;font-size:0.875rem;">
          <span>${text(p.primaryLabel ?? "Primary input")}</span>
          <strong data-ease-roi-value="primary">${text(String(p.primaryDefault ?? 0))}</strong>
        </span>
        <input data-ease-roi-input="primary" type="range" min="${attr(String(p.primaryMin ?? 0))}" max="${attr(String(p.primaryMax ?? 100))}" step="${attr(String(p.primaryStep ?? 1))}" value="${attr(String(p.primaryDefault ?? p.primaryMin ?? 0))}">
      </label>
      <label class="ease-roi-control" style="display:grid;gap:var(--ease-spacing-sm,0.75rem);">
        <span style="display:flex;justify-content:space-between;gap:1rem;font-size:0.875rem;">
          <span>${text(p.secondaryLabel ?? "Secondary input")}</span>
          <strong data-ease-roi-value="secondary">${text(String(p.secondaryDefault ?? 0))}</strong>
        </span>
        <input data-ease-roi-input="secondary" type="range" min="${attr(String(p.secondaryMin ?? 0))}" max="${attr(String(p.secondaryMax ?? 100))}" step="${attr(String(p.secondaryStep ?? 1))}" value="${attr(String(p.secondaryDefault ?? p.secondaryMin ?? 0))}">
      </label>
    </div>
    <div class="ease-roi-result" style="margin-top:var(--ease-spacing-xl,2.5rem);text-align:center;">
      <div style="font-size:0.875rem;color:var(--ease-color-muted-foreground,var(--color-muted-foreground));">${text(p.resultLabel ?? "Projected result")}</div>
      <div style="font-size:clamp(2rem,4vw,3rem);font-weight:800;color:var(--ease-color-primary,var(--color-primary));" data-ease-roi-value="result">0</div>
    </div>
    ${ctaHtml ? `\n    <div style="margin-top:var(--ease-spacing-lg,2rem);text-align:center;">${ctaHtml}</div>` : ""}
  </div>
</section>
${buildScript(uid)}`;
}

registerRenderer("roi-calculator", { render });
